import { ComponentStyleConfig } from '@chakra-ui/react';

import { rem } from '@bonusly/design-system';

export const activeButtonStyles = {
	background: 'red.50',
	color: 'red.500',
};

const createAvatarStyles = (size: number) => ({
	container: {
		fontSize: rem(size / 2.5),
		boxSize: rem(size),
	},
	excessLabel: {
		fontSize: rem(size / 2.5),
		boxSize: rem(size),
	},
	label: {
		fontSize: rem(size / 2.5),
	},
});

const Avatar: ComponentStyleConfig = {
	sizes: {
		'2xs': createAvatarStyles(24),
		xs: createAvatarStyles(32),
		sm: createAvatarStyles(36),
		md: createAvatarStyles(44),
		lg: createAvatarStyles(52),
		xl: createAvatarStyles(64),
		'2xl': createAvatarStyles(72),
	},
};

const Alert: ComponentStyleConfig = {
	baseStyle: {
		container: {
			borderRadius: 'md',
		},
	},
};

const Dropdown: ComponentStyleConfig = {
	parts: ['menu', 'option'],
	baseStyle: {
		menu: {
			ml: 0,
		},
		option: {
			backgroundColor: 'white',
			borderColor: 'gray.50',
			borderStyle: 'solid',
			cursor: 'pointer',
			listStyleType: 'none',
			p: 2,
			transitionDuration: 'normal',
			transitionProperty: 'common',
		},
	},
	sizes: {
		sm: {
			menu: {
				width: { base: '100%', sm: '200px' },
			},
		},
		md: {
			menu: {
				width: { base: '100%', sm: '320px' },
			},
		},
	},
	variants: {
		floating: {
			menu: {
				p: 2,
			},
			option: {
				borderRadius: 'md',
				_focus: { backgroundColor: 'gray.25' },
				_hover: { backgroundColor: 'gray.25' },
			},
		},
		static: {
			option: {
				border: 0,
				borderRadius: 'sm',
				_focus: { backgroundColor: 'gray.25' },
				_hover: { backgroundColor: 'gray.50' },
			},
		},
		staticControlled: {
			option: {
				border: 0,
				borderRadius: 'sm',
			},
		},
		staticControlledHighlight: {
			option: {
				backgroundColor: 'gray.50',
				border: 0,
				borderRadius: 'sm',
			},
		},
	},
	defaultProps: {
		size: 'md',
		variant: 'static',
	},
};

const Media: ComponentStyleConfig = {
	baseStyle: {
		borderRadius: rem(12),
		maxH: rem(512),
	},
};

const Message: ComponentStyleConfig = {
	baseStyle: {
		borderRadius: 'sm',
		display: 'flex',
		mb: 2,
		px: 3,
		py: 1.5,
	},
	variants: {
		error: {
			bgColor: 'red.50',
		},
		success: {
			bgColor: 'green.50',
		},
		warning: {
			bgColor: 'yellow.50',
		},
	},
	defaultProps: {
		variant: 'success',
	},
};

const MessageIcon: ComponentStyleConfig = {
	baseStyle: {
		mr: 3,
		size: 'xl',
		verticalAlign: 'top',
	},
	variants: {
		error: {
			color: 'red.600',
		},
		success: {
			color: 'green.600',
		},
		warning: {
			color: 'yellow.500',
		},
	},
	defaultProps: {
		variant: 'success',
	},
};

const MessageText: ComponentStyleConfig = {
	baseStyle: {
		fontSize: 'sm',
		fontWeight: 'normal',
		lineHeight: 5,
	},
	variants: {
		error: {
			color: 'red.700',
		},
		success: {
			color: 'green.700',
		},
		warning: {
			color: 'yellow.700',
		},
	},
	defaultProps: {
		variant: 'success',
	},
};

const Options: ComponentStyleConfig = {
	baseStyle: { padding: rem(1) },
	variants: {
		outline: {
			border: '1px',
			borderColor: 'gray.100',
			borderRadius: '12px',
		},
		borderless: {},
	},
	defaultProps: {
		variant: 'outline',
	},
};

const Overlay: ComponentStyleConfig = {
	baseStyle: {
		position: 'absolute',
		backgroundColor: 'rgba(14, 15, 16, 0.2)',
		boxSize: '100%',
		zIndex: 'overlay',
	},
};

const Popover: ComponentStyleConfig = {
	parts: ['content'],
	baseStyle: {
		content: {
			backgroundColor: 'white',
			borderColor: 'gray.50',
			borderRadius: 'md',
			boxShadow: 'light.600',
			borderWidth: '1px',
		},
	},
};

const Textarea: ComponentStyleConfig = {
	baseStyle: {
		'::placeholder': {
			color: 'gray.500',
		},
		color: 'gray.900',
	},
};

const Text: ComponentStyleConfig = {
	baseStyle: {
		fontFamily: 'interface',
		wordBreak: 'break-word',
	},
	variants: {
		sm: {
			fontSize: 'sm',
			lineHeight: rem(20),
			fontWeight: 'medium',
		},
		md: {
			fontSize: 'md',
			letterSpacing: '-0.013em',
			lineHeight: '150%',
		},
		lg: {
			fontSize: 'lg',
			letterSpacing: '-0.013em',
			lineHeight: rem(28),
		},
		xl: {
			fontSize: 'xl',
			fontWeight: 'medium',
			letterSpacing: '-0.01em',
			lineHeight: '160%',
		},
		error: {
			color: 'red.700',
		},
	},
	defaultProps: {
		variant: 'xl',
	},
};

const ffDisplay = 'Plus Jakarta Sans, system-ui, sans-serif';
const ffSansSerif = 'Inter, system-ui, sans-serif';

export const Chip: ComponentStyleConfig = {
	parts: ['chip', 'text'],
	baseStyle: {
		chip: {
			alignItems: 'center',
			backgroundColor: 'green.400',
			borderRadius: rem(60),
			color: 'green.700',
			display: 'inline-flex',
			justifyContent: 'center',
		},
		text: {
			display: 'inline-flex',
			fontWeight: 'medium',
		},
	},
	sizes: {
		xs: {
			chip: {
				h: rem(28),
			},
			text: {
				fontSize: 'lg',
				px: 2,
			},
		},
		sm: {
			chip: {
				h: rem(32),
			},
			text: {
				fontSize: 'xl',
				pl: rem(10),
				pr: rem(12),
				py: rem(8),
				lineHeight: rem(24),
			},
		},
		md: {
			chip: {
				h: rem(40),
			},
			text: {
				fontSize: 'md',
				px: 4,
			},
		},
		lg: {
			chip: {
				h: rem(48),
			},
			text: {
				fontSize: rem(24),
				px: 4,
				lineHeight: 32 / 24,
			},
		},
	},
	defaultProps: {
		size: 'sm',
	},
	variants: {
		gray: {
			chip: {
				backgroundColor: 'blackAlpha.50',
				color: 'gray.600',
			},
		},
		green: {
			chip: {
				backgroundColor: 'green.400',
				color: 'green.700',
			},
		},
		amount: {
			chip: {},
			text: {
				fontFamily: 'display',
				fontWeight: 600,
			},
		},
	},
};

const Divider: ComponentStyleConfig = {
	baseStyle: {
		borderColor: 'gray.50',
	},
};

const SpeechBubble: ComponentStyleConfig = {
	baseStyle: {
		background: 'white',
		borderRadius: rem(12),
		boxShadow: 'light.100',
	},
};

const Tooltip: ComponentStyleConfig = {
	baseStyle: {
		backgroundColor: 'rgba(29, 32, 34, 0.9)',
		borderRadius: 'sm',
		color: 'white',
		fontSize: 'sm',
		fontWeight: 'normal',
		maxW: rem(240),
		px: 2,
		py: 1,
	},
};

const Modal: ComponentStyleConfig = {
	baseStyle: {
		body: {
			fontSize: 'xl',
			fontWeight: 'normal',
			px: 5,
			py: 2,
		},
		closeButton: {
			borderRadius: 'full',
			top: 3,
		},
		content: {
			borderRadius: 'md',
		},
		header: {
			borderBottomWidth: '1px',
			borderColor: 'gray.100',
			borderStyle: 'solid',
			color: 'gray.900',
			fontSize: '2xl',
			fontWeight: 'semibold',
		},
	},
};

const Heading: ComponentStyleConfig = {
	sizes: {
		xxl: {
			fontSize: 48,
		},
	},
};

const Link: ComponentStyleConfig = {
	baseStyle: {
		color: 'blue.500',
		_hover: {
			textDecor: 'underline',
		},
	},
};

const FormLabel: ComponentStyleConfig = {
	baseStyle: {
		fontFamily: 'Inter',
		fontStyle: 'normal',
		fontSize: 'sm',
		lineHeight: 'base',
		letterSpacing: '-0.013em',
		color: 'black.500',
	},
};

export const theme = {
	initialColorMode: 'light',
	useSystemColorMode: false,
	fonts: {
		body: ffSansSerif,
		display: ffDisplay,
		interface: ffSansSerif,
	},
	fontWeights: {
		normal: 425,
	},
	radii: {
		none: '0',
		sm: '0.125rem',
		base: '0.25rem',
		md: '0.375rem',
		lg: '0.5rem',
		xl: '0.75rem',
		'2xl': '1rem',
		'3xl': '1.5rem',
		full: '9999px',
	},
	breakpoints: {
		base: '0em',
		'2xs': '23.5em',
		xs: '42em',
		sm: '53.75em',
		md: '64em',
		lg: '80em',
		xl: '90em',
	},
	styles: {
		global: {
			'html, body': {
				overflowAnchor: 'none',
			},
		},
	},
	letterSpacings: {
		tightest: '-0.01em',
	},
	colors: {
		success: {
			text: '#066A35',
			background: '#D9F2E3',
		},
		input: {
			error: {
				background: '#FBF3F3',
			},
		},
		purple: {
			100: '#F1EFFF',
			200: '#D5CEFF',
			300: '#AA9DFF',
			400: '#8E7DFF',
			500: '#8E7DFF',
			600: '#5442CC',
			700: '#5442CC',
			800: '#312579',
			900: '#312579',
			highlight10: '#725cff1a',
			highlight15: '#725cff26',
			highlight20: '#725cff33',
			alpha100: 'rgba(123, 92, 224, 0.08)',
			alpha150: 'rgba(123, 92, 224, 0.12)',
			alpha200: 'rgba(123, 92, 224, 0.12)',
		},
		blue: {
			100: '#E8F1FD',
			200: '#D2E2FC',
			300: '#ACCDFF',
			400: '#6BA6FF',
			500: '#1670DF',
			600: '#1358B9',
			700: '#14458A',
			800: '#103560',
			900: '#061630',
			highlight10: '#1C6FEE1A',
			highlight15: '#1C6FEE26',
			highlight20: '#1C6FEE33',
			alpha100: 'rgba(22, 112, 223, 0.08)',
			alpha150: 'rgba(22, 112, 223, 0.12)',
			alpha200: 'rgba(22, 112, 223, 0.16)',
		},
		green: {
			50: '#D9F2E3',
			100: '#E4FFEF',
			200: '#B6F2CE',
			300: '#90E7B3',
			400: '#4CCF81',
			500: '#118342',
			600: '#066A35',
			700: '#055229',
			800: '#0B5228',
			900: '#042210',
			highlight10: '#16AB521A',
			highlight15: '#16AB5226',
			highlight20: '#16AB5233',
			alpha100: 'rgba(10, 133, 75, 0.08)',
			alpha150: 'rgba(10, 133, 75, 0.12)',
			alpha200: 'rgba(10, 133, 75, 0.16)',
		},
		gray: {
			25: '#F6F6F9',
			50: '#F9FAFB',
			100: '#F4F6FA',
			200: '#EFF0F5',
			300: '#DBE1E6',
			400: '#898F9A',
			500: '#6A707C',
			600: '#687378',
			700: '#586165',
			800: '#3B4044',
			900: '#1D2022',
			950: '#1D2022',
			n500: '#8897A0',
			n600: '#6D787E',
			n900: '#1C1F21',
			highlight10: '#0F10111A',
			highlight15: '#0F101126',
			highlight50: '#0E0F100A',
			alpha100: 'rgba(107, 116, 133, 0.08)',
			alpha200: 'rgba(107, 116, 133, 0.16)',
			alpha300: 'rgba(107, 116, 133, 0.24)',
			alpha500: 'rgba(107, 116, 133, 0.08)',
			alpha600: 'rgba(107, 116, 133, 0.12)',
			alpha700: 'rgba(107, 116, 133, 0.16)',
		},
		red: {
			50: '#F8E7E7',
			100: '#FFF0F1',
			200: '#FECDCF',
			300: '#FDACB0',
			400: '#FC7A80',
			500: '#D82C2F',
			600: '#AD2527',
			700: '#832421',
			800: '#642426',
			900: '#321213',
			highlight10: 'rgba(251, 91, 99, 0.08)',
			highlight20: 'rgba(251, 91, 99, 0.16)',
		},
		yellow: {
			50: '#FAF0C7',
			100: '#FFF7E5',
			200: '#FEEDC3',
			300: '#FFDC8A',
			400: '#FFC847',
			500: '#B37E00',
			600: '#CC9514',
			700: '#5E4003',
			800: '#4C3807',
			900: '#191302',
			alpha100: 'rgba(152, 107, 1, 0.08)',
		},
		black: {
			form: '#14151A',
			light: '#1D1F25',
		},
	},
	shadows: {
		light: {
			100: '0px 0px 1px rgba(67, 72, 86, 0.1), 0px 1px 1px rgba(67, 72, 86, 0.04), 0px 2px 2px rgba(67, 72, 86, 0.03), 0px 5px 3px rgba(67, 72, 86, 0.02), 0px 9px 4px rgba(67, 72, 86, 0.01)',
			200: '0px 19px 6px rgba(67, 72, 86, 0.01), 0px 11px 5px rgba(67, 72, 86, 0.02), 0px 5px 4px rgba(67, 72, 86, 0.04), 0px 1px 2px rgba(67, 72, 86, 0.05), 0px 0px 1px rgba(67, 72, 86, 0.08);',
			400: '0px 0px 1px rgba(67, 72, 86, 0.08), 0px 2px 4px rgba(67, 72, 86, 0.08), 0px 9px 8px rgba(67, 72, 86, 0.08), 0px 21px 12px rgba(67, 72, 86, 0.04), 0px 37px 14px rgba(67, 72, 86, 0.01)',
			600: '0px 56px 22px rgba(67, 72, 86, 0.02), 0px 32px 19px rgba(67, 72, 86, 0.07), 0px 14px 14px rgba(67, 72, 86, 0.12), 0px 4px 8px rgba(67, 72, 86, 0.14), 0px 0px 0px rgba(67, 72, 86, 0.14)',
		},
	},
	sizes: {
		'2xl': '42.5rem',
	},
	components: {
		Avatar,
		Alert,
		Chip,
		Divider,
		Dropdown,
		Heading,
		Link,
		Media,
		Message,
		MessageIcon,
		MessageText,
		Modal,
		Options,
		Overlay,
		Popover,
		SpeechBubble,
		Text,
		Textarea,
		Tooltip,
		FormLabel,
	},
};
